// =============================================================================
// RetentionDefaults (super admin)
//
// Edits the platform-default retention windows that apply to every
// tenant which doesn't have its own override. Reads / writes go through
// the App DB edge function `admin-retention-policies` with
// set_by_role='super_admin', which the function uses both to enforce
// "tenant_admin can't edit the platform default" and to stamp the
// admin_audit_log entry correctly.
//
// Tenants editing their OWN overrides do that from
// platform.../admin/tenants/<id>/manage/settings → Data Retention
// section. This page is super-admin scoped and edits the (NULL=platform
// default) rows.
// =============================================================================

import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Database, Plane, FileText, Hotel, ShieldCheck, Receipt,
  AlertOctagon, RefreshCw, Save, UserX, Play, Trash2, ShieldAlert,
  MessageSquare,
} from 'lucide-react';
import { Spinner } from '@/components/shared/UIComponents';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { useAuth } from '@/contexts/AuthContext';
import {
  listRetentionPolicies,
  upsertRetentionPolicy,
  fetchPlatformSettings,
  updateAnonRetentionDefault,
  updateAnonSignupsPerIpPerDay,
  updateFeedbackEnabledDefault,
  updateAnonCleanupHourDefault,
  runAnonCleanup,
  type RetentionPolicyRow,
  type PlatformSettingsRow,
  type AnonCleanupResult,
} from '@/lib/api';
import { errorMessage } from '@/lib/errorMessage';
import { formatScheduleConversions } from '@/lib/timeFormat';

const ARTIFACT_TYPES: Array<{
  key:         string;
  label:       string;
  icon:        any;
  description: string;
  recommendation: string;
}> = [
  { key: 'boarding_pass_image',       label: 'Boarding pass images',     icon: Plane,
    description: 'Photos in the boarding-passes bucket. Flight record stays.',
    recommendation: '90 days — covers expense-report windows.' },
  { key: 'scanned_doc_boarding_pass', label: 'Scanned boarding passes',  icon: Plane,
    description: 'Document scanner uploads tagged as boarding pass.',
    recommendation: '90 days — same shelf life as the image.' },
  { key: 'scanned_doc_visa',          label: 'Scanned visas',            icon: ShieldCheck,
    description: 'Visas. Referenced for years; default keeps them forever.',
    recommendation: 'Keep forever or 730 days minimum.' },
  { key: 'scanned_doc_passport',      label: 'Scanned passports',        icon: FileText,
    description: 'Passport pages. Foundational ID; default keeps them forever.',
    recommendation: 'Keep forever or 730 days minimum.' },
  { key: 'scanned_doc_hotel',         label: 'Hotel confirmations',      icon: Hotel,
    description: 'Confirmation emails / printouts.',
    recommendation: '180 days — chargeback / dispute window.' },
  { key: 'scanned_doc_insurance',     label: 'Travel insurance docs',    icon: Receipt,
    description: 'Insurance certificates.',
    recommendation: '365 days — late-claim window.' },
  { key: 'scanned_doc_other',         label: 'Other scanned documents',  icon: FileText,
    description: 'Uncategorized travel docs.',
    recommendation: '365 days — catch-all.' },
  { key: 'disruption_event',          label: 'Disruption events',        icon: AlertOctagon,
    description: 'Cancellation / diversion / delay history.',
    recommendation: '180 days — pattern analysis.' },
  { key: 'trip_flight_record',        label: 'Trip flight records',      icon: Plane,
    description: 'Tiny rows; journal value.',
    recommendation: 'Keep forever.' },
];

export default function RetentionDefaultsPage() {
  const { user } = useAuth();
  const email = user?.email || 'unknown';

  const [rows,    setRows]    = useState<RetentionPolicyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [editVal, setEditVal] = useState('');
  const [editKeep, setEditKeep] = useState(false);

  // ── Anonymous-user retention (separate from per-artifact policy)
  // Lives in platform_settings.anon_retention_days on the Platform DB.
  // Surfaced here for super admins; tenant override is editable from
  // each tenant's Branding page.
  const [anonRow,        setAnonRow]        = useState<PlatformSettingsRow | null>(null);
  const [anonEditing,    setAnonEditing]    = useState(false);
  const [anonEditVal,    setAnonEditVal]    = useState('');
  const [anonSaving,     setAnonSaving]     = useState(false);

  // ── Platform-wide per-IP cap (anon signups per hashed IP per 24h) ──
  const [ipCapEditing,  setIpCapEditing]  = useState(false);
  const [ipCapEditVal,  setIpCapEditVal]  = useState('');
  const [ipCapSaving,   setIpCapSaving]   = useState(false);

  // ── Platform-wide Feedback toggle ──
  const [feedbackSaving, setFeedbackSaving] = useState(false);
  const toggleFeedbackDefault = async (next: boolean) => {
    setFeedbackSaving(true);
    try {
      const updated = await updateFeedbackEnabledDefault(next, user?.id ?? null);
      setAnonRow(updated);
    } catch (e) {
      console.error('[retention-defaults] toggle feedback failed', e);
      alert(`Failed to save: ${errorMessage(e)}`);
    } finally {
      setFeedbackSaving(false);
    }
  };

  // ── Manual cleanup run (super admin: across all tenants) ───────────
  const [cleanupRunning, setCleanupRunning] = useState(false);
  const [cleanupResult,  setCleanupResult]  = useState<AnonCleanupResult | null>(null);
  const [cleanupError,   setCleanupError]   = useState<string | null>(null);
  // Confirm modal shown before a destructive (non-dry-run) cleanup so the
  // super admin sees the scope and clicks through deliberately.
  const [confirmOpen,    setConfirmOpen]    = useState(false);

  // ── Scheduled-run hour (UTC) ────────────────────────────────────────
  // Drives the platform default for pg_cron 'anon-cleanup-hourly'.
  // Tenants can override per-tenant from their Data Retention page.
  const [hourSaving, setHourSaving] = useState(false);
  const saveCleanupHour = async (hourUtc: number) => {
    setHourSaving(true);
    try {
      const updated = await updateAnonCleanupHourDefault(hourUtc, user?.id ?? null);
      setAnonRow(updated);
    } catch (e) {
      console.error('[retention-defaults] save cleanup hour failed', e);
      alert(`Failed to save: ${errorMessage(e)}`);
    } finally {
      setHourSaving(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Pass null = platform-default rows only
      const [policyRows, platformSettings] = await Promise.all([
        listRetentionPolicies(null),
        fetchPlatformSettings(),
      ]);
      setRows(policyRows);
      setAnonRow(platformSettings);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Shared core that actually runs the cleanup. The buttons call this
  // directly for dry-run; the destructive variant goes through the
  // confirm modal first (see confirmOpen).
  const runCleanup = async (dryRun: boolean) => {
    setCleanupRunning(true);
    setCleanupError(null);
    setCleanupResult(null);
    try {
      const r = await runAnonCleanup({ dryRun });
      setCleanupResult(r);
    } catch (e) {
      console.error('[retention-defaults] cleanup failed', e);
      setCleanupError(errorMessage(e));
      throw e;  // bubble so the ConfirmDialog keeps itself open
    } finally {
      setCleanupRunning(false);
    }
  };

  const saveIpCap = async () => {
    const val = parseInt(ipCapEditVal.trim(), 10);
    if (!Number.isInteger(val) || val < 1 || val > 1000) {
      alert('Enter a number between 1 and 1000.');
      return;
    }
    setIpCapSaving(true);
    try {
      const updated = await updateAnonSignupsPerIpPerDay(val, user?.id ?? null);
      setAnonRow(updated);
      setIpCapEditing(false);
    } catch (e) {
      console.error('[retention-defaults] save ip cap failed', e);
      alert(`Failed to save: ${errorMessage(e)}`);
    } finally {
      setIpCapSaving(false);
    }
  };

  // ConfirmDialog gate for saving a 0-day platform default. The
  // modal calls performSaveAnon(0) when confirmed.
  const [confirmZeroOpen, setConfirmZeroOpen] = useState(false);

  const performSaveAnon = async (days: number) => {
    setAnonSaving(true);
    try {
      const updated = await updateAnonRetentionDefault(days, user?.id ?? null);
      setAnonRow(updated);
      setAnonEditing(false);
    } catch (e) {
      console.error('[retention-defaults] save anon failed', e);
      alert(`Failed to save: ${errorMessage(e)}`);
      throw e;  // keep ConfirmDialog open on failure
    } finally {
      setAnonSaving(false);
    }
  };

  const saveAnon = async () => {
    const days = parseInt(anonEditVal.trim(), 10);
    if (!Number.isInteger(days) || days < 0 || days > 3650) {
      alert('Enter a number between 0 and 3650 days. 0 = purge every inactive guest immediately.');
      return;
    }
    if (days === 0) {
      // Hand off to the ConfirmDialog instead of window.confirm so
      // the warning matches the rest of the page's modal styling.
      setConfirmZeroOpen(true);
      return;
    }
    await performSaveAnon(days);
  };

  const formatDays = (d: number | null | undefined) => {
    if (d == null) return 'Keep forever';
    if (d % 365 === 0) return `${d / 365} year${d / 365 === 1 ? '' : 's'}`;
    if (d % 30  === 0) return `${d / 30}  month${d / 30  === 1 ? '' : 's'}`;
    return `${d} day${d === 1 ? '' : 's'}`;
  };

  const beginEdit = (key: string, current: number | null | undefined) => {
    setEditing(key);
    setEditVal(current != null ? String(current) : '');
    setEditKeep(current === null);
  };

  const save = async (key: string) => {
    setSaving(key);
    try {
      const days = editKeep
        ? null
        : editVal.trim() === '' ? null
        : Math.max(1, parseInt(editVal.trim(), 10) || 0);
      await upsertRetentionPolicy({
        tenant_id:      null,            // null = platform default
        artifact_type:  key,
        retention_days: days,
        set_by_role:    'super_admin',
        set_by_email:   email,
      });
      setEditing(null);
      await load();
    } catch (e) {
      console.error('[retention-defaults] save failed', e);
      alert(`Failed to save: ${errorMessage(e)}`);
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-surface-500 hover:text-surface-900 mb-4">
        <ArrowLeft size={14} />
        Back to admin
      </Link>

      <div className="flex items-center gap-3 mb-2">
        <Database size={24} className="text-brand-600" />
        <h1 className="text-2xl font-bold text-surface-900">Data Retention — Platform Defaults</h1>
      </div>
      <p className="text-sm text-surface-500 mb-6">
        These windows apply to every tenant unless they've set their own override.
        Tenant overrides are managed from each tenant's individual Settings page.
        Minimum 1 day. Empty / "Keep forever" pins the artifact indefinitely
        (subject to per-row Keep-Forever flags users may set).
      </p>

      {/* ── Feedback toggle (platform default) ──────────────────────
          Not strictly a retention setting, but lives on the same
          super-admin page since it follows the identical platform-
          default + tenant-override pattern. Controls whether the
          FeedbackModal renders on tenant home pages by default. */}
      {!loading && anonRow && (
        <div className="glass-panel p-4 mb-6 border-l-2 border-l-brand-600">
          <div className="flex items-start gap-3">
            <MessageSquare size={18} className="text-brand-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-surface-900 mb-0.5">User feedback collection</h3>
              <p className="text-xs text-surface-500 mb-2">
                When enabled, end users see a floating "Feedback" button on the home page and can leave comments + an optional rating.
                Submissions show up in the Feedback tab on every tenant. Tenants can override this default per-tenant.
              </p>
              <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={anonRow.feedback_enabled}
                  onChange={(e) => toggleFeedbackDefault(e.target.checked)}
                  disabled={feedbackSaving}
                  className="w-4 h-4 rounded text-brand-600 focus:ring-2 focus:ring-brand-500"
                />
                <span className="text-sm font-medium text-surface-900">
                  Show feedback button on tenant home pages
                </span>
                {feedbackSaving && <Spinner size="sm" />}
              </label>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="md" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* ════════════════════════════════════════════════════════════
              SECTION: Anonymous (Guest) Accounts
              Retention default + per-IP cap + manual cleanup all share
              the same subject. Grouped under one labeled section so a
              super admin doing guest-related ops sees them together.
              ════════════════════════════════════════════════════════════ */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 mb-1">
              <UserX size={16} className="text-brand-600" />
              <h2 className="text-sm font-semibold uppercase tracking-wider text-surface-500">
                Anonymous (Guest) Accounts
              </h2>
            </div>

          {/* ── Anonymous-user (guest) retention ─────────────────────────
              Special row above the per-artifact policies because it
              governs the USER account itself (Supabase anonymous
              sign-in) rather than any single artifact tied to the user. */}
          <div className="glass-panel p-4 border-l-2 border-l-brand-600">
            <div className="flex items-start gap-3">
              <UserX size={18} className="text-brand-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-surface-900 mb-0.5">
                  Anonymous (guest) accounts
                </h3>
                <p className="text-xs text-surface-500 mb-1">
                  Visitors who tap "Try the App" get a guest account. When inactive for this long, the cleanup cron removes them and their data.
                </p>
                <p className="text-[11px] text-surface-400 mb-2 italic">
                  30 days — long enough that engaged users return, short enough that abandoned accounts don't pile up.
                </p>

                {!anonEditing && (
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className={`text-sm font-bold ${anonRow?.anon_retention_days === 0 ? 'text-red-600' : 'text-surface-900'}`}>
                      {anonRow
                        ? (anonRow.anon_retention_days === 0
                            ? 'Purge any inactive guest (0 days)'
                            : `${anonRow.anon_retention_days} day${anonRow.anon_retention_days === 1 ? '' : 's'} of inactivity`)
                        : <span className="text-amber-600">Not migrated — using hardcoded 30-day fallback</span>}
                    </span>
                    {anonRow && (
                      <button
                        onClick={() => {
                          setAnonEditing(true);
                          setAnonEditVal(String(anonRow.anon_retention_days));
                        }}
                        className="ml-auto text-xs text-brand-600 hover:underline"
                      >
                        Edit default
                      </button>
                    )}
                  </div>
                )}
                {anonRow?.anon_retention_days === 0 && !anonEditing && (
                  <div className="mt-1 text-[11px] text-red-600">
                    With 0-day retention, every guest who isn't actively signed in becomes eligible the next time cleanup runs.
                  </div>
                )}

                {anonEditing && (
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <label className="text-xs text-surface-500">Days of inactivity:</label>
                      <input
                        type="number"
                        min={0}
                        max={3650}
                        value={anonEditVal}
                        onChange={(e) => setAnonEditVal(e.target.value)}
                        placeholder="e.g. 30"
                        className="w-24 px-2 py-1 text-sm bg-surface-0 text-surface-900 border border-surface-300 rounded-md"
                      />
                      <span className="text-[11px] text-surface-500">(0 = purge any inactive guest)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={saveAnon} disabled={anonSaving} className="btn-primary text-xs">
                        {anonSaving ? <Spinner size="sm" /> : <Save size={12} />}
                        Save
                      </button>
                      <button
                        onClick={() => setAnonEditing(false)}
                        className="text-xs text-surface-500 hover:text-surface-900"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Per-IP signup cap (platform-wide abuse guard) ───────────
              Caps how many anonymous signups can come from the same
              hashed IP in a rolling 24h window. Hardcoded to 3 before
              this card existed; now configurable so operators can
              raise it for shared-IP scenarios or lower it under
              active attack. The edge function clamps to [1,1000]
              server-side regardless of what the client sends. */}
          <div className="glass-panel p-4 border-l-2 border-l-amber-500">
            <div className="flex items-start gap-3">
              <ShieldAlert size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-surface-900 mb-0.5">
                  Signups per IP (per 24h)
                </h3>
                <p className="text-xs text-surface-500 mb-1">
                  Platform-wide cap: how many guest accounts can be created from the same hashed
                  device identifier in a rolling 24-hour window. Same value applies to every
                  tenant. Tenant-level caps (set on each tenant's Data Retention page) layer on
                  top of this.
                </p>
                <p className="text-[11px] text-surface-400 mb-2 italic">
                  3 = default. Raise to 10+ for shared-IP scenarios (co-working spaces, demos).
                  Lower to 1 during an active attack.
                </p>

                {!ipCapEditing && (
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-sm font-bold text-surface-900">
                      {anonRow
                        ? `${anonRow.anon_signups_per_ip_per_day} signup${anonRow.anon_signups_per_ip_per_day === 1 ? '' : 's'} / IP / 24h`
                        : <span className="text-amber-600">Not migrated — using hardcoded 3</span>}
                    </span>
                    {anonRow && (
                      <button
                        onClick={() => {
                          setIpCapEditing(true);
                          setIpCapEditVal(String(anonRow.anon_signups_per_ip_per_day));
                        }}
                        className="ml-auto text-xs text-brand-600 hover:underline"
                      >
                        Edit cap
                      </button>
                    )}
                  </div>
                )}

                {ipCapEditing && (
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <label className="text-xs text-surface-500">Max signups per IP / 24h:</label>
                      <input
                        type="number"
                        min={1}
                        max={1000}
                        value={ipCapEditVal}
                        onChange={(e) => setIpCapEditVal(e.target.value)}
                        placeholder="3"
                        className="w-24 px-2 py-1 text-sm bg-surface-0 text-surface-900 border border-surface-300 rounded-md"
                      />
                      <span className="text-[11px] text-surface-500">(1–1000)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={saveIpCap} disabled={ipCapSaving} className="btn-primary text-xs">
                        {ipCapSaving ? <Spinner size="sm" /> : <Save size={12} />}
                        Save
                      </button>
                      <button
                        onClick={() => setIpCapEditing(false)}
                        className="text-xs text-surface-500 hover:text-surface-900"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Manual cleanup run (anonymous accounts only) ────────────
              Lets the super admin trigger the same cleanup the scheduler
              would run, across all tenants. Dry-run mode previews
              candidates without deleting. Moved up from the bottom of
              the page so all three guest-account controls live
              together. */}
          <div className="glass-panel p-5">
            <div className="flex items-start gap-3 mb-3">
              <UserX size={20} className="text-brand-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-base font-semibold text-surface-900">Run anonymous-user cleanup</h3>
                <p className="text-xs text-surface-500 mt-0.5">
                  Across all tenants, finds guest accounts inactive longer than each tenant's effective
                  retention window and deletes them (cascades to profile, tenant_users, and trips).
                </p>
              </div>
            </div>

            {/* Schedule control — hourly cron picks up tenants whose
                effective UTC hour matches the current tick. This sets
                the PLATFORM DEFAULT; tenant admins can override on
                their own Data Retention page. Changes take effect on
                the next hourly cron tick. */}
            <div className="flex flex-wrap items-center gap-3 mb-3 p-3 rounded-md bg-surface-50 border border-surface-200">
              <span className="text-xs font-medium text-surface-700">Scheduled run time</span>
              <label className="inline-flex items-center gap-2 text-xs">
                <select
                  value={anonRow?.anon_cleanup_hour_utc ?? 4}
                  onChange={e => saveCleanupHour(parseInt(e.target.value, 10))}
                  disabled={hourSaving}
                  // Explicit text-surface-900 + bg-surface-0 — without
                  // them the select inherited the parent's white text
                  // on its own white background and was invisible.
                  // Matches the editable-number-input pattern used by
                  // the retention-days control above (line ~397).
                  className="px-2 py-1 rounded-md border border-surface-300 text-xs font-mono bg-surface-0 text-surface-900 focus:outline-none focus:ring-2 focus:ring-brand-500/40 disabled:opacity-50"
                >
                  {Array.from({ length: 24 }, (_, h) => (
                    <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
                  ))}
                </select>
                <span className="text-surface-500">UTC daily</span>
                {hourSaving && <Spinner size="sm" />}
              </label>
              <span className="text-[11px] text-surface-500">
                Platform default — tenants can override on their own Data Retention page.
              </span>
              {/* Conversion hint — translates the stored UTC hour into
                  US Eastern (both EDT and EST) and India IST so the
                  operator doesn't have to do mental math. Recomputes
                  on every change. */}
              <span className="block w-full text-[11px] text-surface-500 mt-1">
                {formatScheduleConversions(anonRow?.anon_cleanup_hour_utc ?? 4)}
              </span>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => runCleanup(true)}
                disabled={cleanupRunning}
                className="px-3 py-1.5 rounded-md text-xs font-medium bg-surface-100 hover:bg-surface-200 text-surface-900 inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                {cleanupRunning ? <Spinner size="sm" /> : <Play size={12} />}
                Dry run (preview only)
              </button>
              <button
                onClick={() => setConfirmOpen(true)}
                disabled={cleanupRunning}
                className="px-3 py-1.5 rounded-md text-xs font-medium bg-red-600 hover:bg-red-700 text-white inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                {cleanupRunning ? <Spinner size="sm" /> : <Trash2 size={12} />}
                Run cleanup now
              </button>
            </div>

            {cleanupError && (
              <div className="mt-3 p-3 rounded-md bg-red-50 border border-red-200 text-xs text-red-700">
                {cleanupError}
              </div>
            )}

            {cleanupResult && (
              <div className="mt-3 p-3 rounded-md bg-surface-50 border border-surface-200 text-xs space-y-2">
                <div className="font-semibold text-surface-900">
                  {cleanupResult.dryRun ? 'Dry run results' : 'Cleanup complete'}
                </div>
                <div className="text-surface-600">
                  Tenants scanned: <strong>{cleanupResult.tenantsScanned}</strong>
                  {' · '}
                  {cleanupResult.dryRun ? 'Would delete' : 'Deleted'}: <strong>{cleanupResult.dryRun ? cleanupResult.totalCandidates : cleanupResult.totalDeleted}</strong> account(s)
                </div>
                {cleanupResult.perTenant
                  .filter(t => t.candidates.length > 0 || t.errors.length > 0 || t.skipped)
                  .slice(0, 20)
                  .map(t => (
                    <div key={t.tenant_id} className="text-[11px] text-surface-500 border-t border-surface-200 pt-1.5">
                      <div>
                        <span className="font-mono">{t.tenant_id.slice(0, 8)}…</span>
                        {' '}({t.anon_retention_days}d):{' '}
                        {t.skipped
                          ? <span className="text-amber-600">skipped — {t.skipped}</span>
                          : (
                            <>
                              {t.candidates.length} candidate{t.candidates.length === 1 ? '' : 's'}
                              {!cleanupResult.dryRun && `, ${t.deleted.length} deleted`}
                              {t.errors.length > 0 && (
                                <span className="text-red-600">, {t.errors.length} error{t.errors.length === 1 ? '' : 's'}</span>
                              )}
                            </>
                          )}
                      </div>
                      {t.errors.length > 0 && (
                        <div className="mt-1 ml-3 space-y-0.5">
                          {t.errors.slice(0, 5).map((e, i) => (
                            <div key={i} className="text-red-600 font-mono break-all">
                              {e.user_id !== '-' && <span className="text-red-400">{e.user_id.slice(0, 8)}…: </span>}
                              {e.error}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            )}
          </div>
          {/* end SECTION: Anonymous (Guest) Accounts */}
          </div>

          {/* ════════════════════════════════════════════════════════════
              SECTION: Per-Artifact Retention
              Platform defaults for trip-related artifacts (boarding
              passes, scanned docs, etc). Tenants edit overrides on
              their own DataRetention page.
              ════════════════════════════════════════════════════════════ */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 mb-1">
              <Database size={16} className="text-brand-600" />
              <h2 className="text-sm font-semibold uppercase tracking-wider text-surface-500">
                Per-Artifact Retention
              </h2>
            </div>

          {ARTIFACT_TYPES.map(({ key, label, icon: Icon, description, recommendation }) => {
            const row       = rows.find(r => r.artifact_type === key && r.tenant_id === null);
            const current   = row?.retention_days;
            const isEditing = editing === key;
            const isSaving  = saving  === key;
            return (
              <div key={key} className="glass-panel p-4">
                <div className="flex items-start gap-3">
                  <Icon size={18} className="text-brand-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-surface-900 mb-0.5">{label}</h3>
                    <p className="text-xs text-surface-500 mb-1">{description}</p>
                    <p className="text-[11px] text-surface-400 mb-2 italic">{recommendation}</p>

                    {!isEditing && (
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className={`text-sm font-bold ${current === null ? 'text-emerald-600' : 'text-surface-900'}`}>
                          {row ? formatDays(current) : <span className="text-amber-600">Not set (cleanup uses hardcoded fallback)</span>}
                        </span>
                        <button
                          onClick={() => beginEdit(key, current)}
                          className="ml-auto text-xs text-brand-600 hover:underline"
                        >
                          {row ? 'Edit default' : 'Set default'}
                        </button>
                      </div>
                    )}

                    {isEditing && (
                      <div className="mt-2 space-y-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <label className="text-xs text-surface-500">Days:</label>
                          <input
                            type="number"
                            min={1}
                            value={editKeep ? '' : editVal}
                            disabled={editKeep}
                            onChange={(e) => setEditVal(e.target.value)}
                            placeholder="e.g. 90"
                            className="w-24 px-2 py-1 text-sm bg-surface-0 text-surface-900 border border-surface-300 rounded-md disabled:opacity-50"
                          />
                          <label className="inline-flex items-center gap-1 text-xs text-surface-700">
                            <input
                              type="checkbox"
                              checked={editKeep}
                              onChange={(e) => setEditKeep(e.target.checked)}
                            />
                            Keep forever
                          </label>
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => save(key)} disabled={isSaving} className="btn-primary text-xs">
                            {isSaving ? <Spinner size="sm" /> : <Save size={12} />}
                            Save
                          </button>
                          <button
                            onClick={() => setEditing(null)}
                            className="text-xs text-surface-500 hover:text-surface-900"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          </div>
          {/* end SECTION: Per-Artifact Retention */}
        </div>
      )}

      <div className="mt-6 p-4 bg-surface-100 border border-surface-200 rounded-lg flex items-start gap-2 text-xs text-surface-600">
        <RefreshCw size={12} className="mt-0.5 flex-shrink-0" />
        <span>
          The cleanup cron in the App DB runs daily at 03:30 UTC. Changes take effect on the next run.
          Tenant overrides (set on individual tenant pages) win over these defaults.
        </span>
      </div>

      {/* Destructive-action confirm modal — replaces the native
          window.confirm() so the wording, theming, and async loading
          state all stay in-app. Throws from runCleanup keep the dialog
          open so the user can see the error and retry. */}
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => runCleanup(false)}
        title={anonRow?.anon_retention_days === 0
          ? 'Purge ALL inactive guest accounts?'
          : 'Delete inactive guest accounts?'}
        description={
          <>
            {anonRow?.anon_retention_days === 0 ? (
              <>
                The platform default is currently <strong>0 days</strong>. That means <strong>every guest who isn't actively signed in this moment</strong> on any tenant inheriting the default will be deleted. Tenants with their own override use their own value.
                <br />
                <br />
              </>
            ) : null}
            This will <strong>permanently delete</strong> anonymous guest accounts that have
            been inactive longer than each tenant's retention window — <strong>across all tenants</strong>.
            Profiles, tenant memberships, and saved trips for those users will be removed via cascade.
            <br />
            <br />
            This action cannot be undone. Consider running a Dry run first to preview the
            number of accounts that will be deleted.
          </>
        }
        confirmLabel={anonRow?.anon_retention_days === 0 ? 'Yes, purge now' : 'Yes, run cleanup'}
        cancelLabel="Cancel"
        tone="danger"
      />

      {/* Save-time confirm for setting the platform default to 0.
          The save itself doesn't delete anyone, but the next cleanup
          run will sweep up every inactive guest on every tenant that
          inherits the default. Make that consequence loud before
          the row goes in. */}
      <ConfirmDialog
        open={confirmZeroOpen}
        onClose={() => setConfirmZeroOpen(false)}
        onConfirm={() => performSaveAnon(0)}
        title="Save 0-day platform default?"
        description={
          <>
            Saving <strong>0</strong> means every guest on every tenant inheriting this default
            (i.e. no tenant override) becomes <strong>eligible for deletion the next time
            cleanup runs</strong>.
            <br />
            <br />
            The save itself doesn't delete anyone — but a scheduled or manual cleanup will
            sweep them all up. You can change the value back to a higher number before that
            happens.
          </>
        }
        confirmLabel="Yes, save"
        cancelLabel="Cancel"
        tone="danger"
      />
    </div>
  );
}
